import {
  ICreateDocumentSubmissionInput,
  ICreateSubmissionInput,
  IDocumentSubmission,
  IProcurementSubmission,
  ISubmission,
  ITechnicalSubmission,
} from 'types/submissions'
import {
  DocumentRequirement,
  IProcurementRequirement,
  ITechnicalRequirement,
} from 'types/lots'
import { ETenderRequirementType } from 'types/tender/tenderRequirements'
import { v4 as uuidv4 } from 'uuid'

export enum AnswerExistance {
  EXIST = 'EXIST',
  NOT_EXIST = 'NOT_EXIST',
}
export const AnswerExistanceOptions = [
  AnswerExistance.EXIST,
  AnswerExistance.NOT_EXIST,
]

export type DocumentSubmissionRequirement = {
  id: string
  ordinal: number
  type: ETenderRequirementType
  extraInfo: string | null
  extraFileId: string | null
  lotId: string
  documentRequirementItems: DocumentSubmissionRequirementItem[]
}

export type DocumentSubmissionRequirementItem = {
  id?: string
  documentRequirementId?: string
  ordinal?: number
  criteria: string
  eligibility: string
  exist: AnswerExistance | string
  note: string
  submittedDocumentFileId: string
  evaluationNote: string
}

export type TechnicalSubmissionRequirementItem = {
  criteria: string
  eligibility: string
  point: number
  ordinal?: number
  id: string
  reply: string
  note: string
  submittedDocumentFileId: string
}

export type TechnicalSubmissionRequirement = {
  id: string
  extraInfo: string
  extraFileId: string | null
  lotId: string
  technicalRequirementItems: TechnicalSubmissionRequirementItem[]
}

export type ProcurementSubmissionRequirementItem = {
  description: string
  id: string
  measurement: string
  name: string
  ordinal: number
  quantity: number
  comment: string
  price: number
  totalPrice: number
  submittedDocumentFileId?: string
  vatTotalPrice?: number
}

export type ProcurementSubmissionRequirement = {
  description: null | string
  executionGuarantee: string
  extraFileId: null | string
  extraInfo: null | string
  guaranteeAmount: number
  guaranteeValidityDurationDays: number
  id: string
  lotId: string
  priceDescription: string
  validityDurationDays: number
  procurementRequirementItems: ProcurementSubmissionRequirementItem[]
  //new fields
  commentOnOfferValue: string
  commentOnOfferGuarantee: string
  offerGuaranteeFileId: null | string
  vatIncluded: boolean
}

type MapToDefaultValuesInput = {
  documentRequirements: DocumentRequirement[]
  documentSubmissions: IDocumentSubmission[]
}

export const mapToDefaultValues = ({
  documentRequirements,
  documentSubmissions,
}: MapToDefaultValuesInput): DocumentSubmissionRequirement[] => {
  const modifiedRequirements = documentRequirements.map((requirement) => {
    const mergedDocumentRequirementItems = requirement?.documentRequirementItems.map(
      (item) => {
        //find related submissionItem and use it's values in update mode
        const submissionItem = documentSubmissions
          .flatMap((sub) => sub.documentSubmissionItems)
          .find((subItem) => subItem.documentRequirementItemId === item.id)

        return {
          ...item,
          exist:
            submissionItem && submissionItem.note
              ? AnswerExistance.EXIST
              : AnswerExistance.NOT_EXIST,
          note: submissionItem ? submissionItem.note : '',
          submittedDocumentFileId: submissionItem
            ? submissionItem.submittedDocumentFileId
            : '',
          evaluationNote: submissionItem?.evaluationNote ?? '',
        }
      }
    )

    return {
      ...requirement,
      documentRequirementItems: mergedDocumentRequirementItems,
    }
  })

  return modifiedRequirements
}

export const mapDocumentSubmissionToPayload = (
  input: DocumentSubmissionRequirement[],
  submission: ISubmission
): ICreateSubmissionInput => {
  const documentSubmissions: ICreateDocumentSubmissionInput[] = input.map(
    (document, index) => {
      const generatedID = submission.documentSubmissions[index]?.id || uuidv4()
      return {
        id: generatedID,
        documentRequirementId: document.id,
        submissionId: submission.id,
        documentSubmissionItems: document?.documentRequirementItems.map(
          (item, subIndex) => ({
            id:
              submission.documentSubmissions[index]?.documentSubmissionItems[
                subIndex
              ]?.id ?? uuidv4(),
            note: item.note ? item.note : '',
            submittedDocumentFileId: item.submittedDocumentFileId,
            documentSubmissionId: generatedID,
            documentRequirementItemId: item.id || '',
            evaluationNote: item.evaluationNote || '',
          })
        ),
      }
    }
  )

  return {
    ...submission,
    documentSubmissions,
  }
}

export const mapToTechnicalSubmissionDefaultValues = ({
  technicalRequirement,
  technicalSubmission,
}: {
  technicalRequirement: ITechnicalRequirement
  technicalSubmission: ITechnicalSubmission
}): TechnicalSubmissionRequirement => {
  const mergedTechnicalRequirementItems =
    technicalRequirement?.technicalRequirementItems.map((item) => {
      //find related submissionItem and use it's values in update mode
      const submissionItem = technicalSubmission?.technicalSubmissionItems?.find(
        (subItem) => subItem.technicalRequirementItemId === item.id
      )

      return {
        ...item,
        reply: submissionItem ? submissionItem.reply : '',
        note: submissionItem ? submissionItem.note : '',
        submittedDocumentFileId: submissionItem
          ? submissionItem.submittedDocumentFileId
          : '',
      }
    })

  return {
    ...technicalRequirement,
    technicalRequirementItems: mergedTechnicalRequirementItems,
  }
}

export const mapTechnicalSubmissionToPayload = (
  input: TechnicalSubmissionRequirement,
  submission: ISubmission
) => {
  const generatedID = submission.technicalSubmission?.id || uuidv4()

  return {
    id: generatedID,
    technicalRequirementId: input.id,
    submissionId: submission.id,
    technicalSubmissionItems: input.technicalRequirementItems.map((item, index) => ({
      id:
        submission.technicalSubmission?.technicalSubmissionItems[index]?.id ??
        uuidv4(),
      reply: item.reply ? item.reply : '',
      note: item.note ? item.note : '',
      submittedDocumentFileId: item.submittedDocumentFileId,
      technicalSubmissionId: generatedID,
      technicalRequirementItemId: item.id || '',
    })),
  }
}

export const mapToProcurementSubmissionDefaultValues = ({
  procurementRequirement,
  procurementSubmission,
}: {
  procurementRequirement: IProcurementRequirement
  procurementSubmission: IProcurementSubmission
}): ProcurementSubmissionRequirement => {
  const mergedProcurementRequirementItems =
    procurementRequirement.procurementRequirementItems.map((item) => {
      const submissionItem = procurementSubmission?.procurementSubmissionItems?.find(
        (subItem) => subItem.procurementRequirementItemId === item.id
      )

      return {
        comment: submissionItem ? submissionItem.comment : '',
        price: submissionItem ? submissionItem.price : 0,
        totalPrice: submissionItem ? submissionItem.totalPrice : 0,
        submittedDocumentFileId: submissionItem
          ? submissionItem.submittedDocumentFileId
          : '',
        ...item,
      }
    })

  return {
    ...procurementRequirement,
    commentOnOfferValue: procurementSubmission
      ? procurementSubmission.commentOnOfferValue
      : '',
    commentOnOfferGuarantee: procurementSubmission
      ? procurementSubmission.commentOnOfferGuarantee
      : '',
    offerGuaranteeFileId: procurementSubmission
      ? procurementSubmission.offerGuaranteeFileId
      : '',
    vatIncluded: procurementSubmission ? procurementSubmission.vatIncluded : false,
    procurementRequirementItems: mergedProcurementRequirementItems,
  }
}

export const mapProcurementSubmissionToPayload = (
  input: ProcurementSubmissionRequirement,
  submission: ISubmission
): IProcurementSubmission => {
  const generatedID = submission.procurementSubmission?.id || uuidv4()

  return {
    id: generatedID,
    procurementRequirementId: input.id,
    submissionId: submission.id,
    offerGuaranteeFileId: input.offerGuaranteeFileId,
    commentOnOfferValue: input.commentOnOfferValue,
    commentOnOfferGuarantee: input.commentOnOfferGuarantee,
    vatIncluded: input.vatIncluded,
    procurementSubmissionItems: input.procurementRequirementItems.map(
      (item, index) => ({
        id:
          submission.procurementSubmission?.procurementSubmissionItems[index]?.id ??
          uuidv4(),
        procurementSubmissionId: generatedID,
        submittedDocumentFileId: item.submittedDocumentFileId,
        procurementRequirementItemId: item.id || '',
        comment: item.comment ? item.comment : '',
        price: item.price ? item.price : 0,
        totalPrice: item.totalPrice ? item.totalPrice : 0,
        vatTotalPrice: item.vatTotalPrice ? item.vatTotalPrice : 0,
      })
    ),
  }
}
